import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';
import { EntityState, createEntityAdapter } from '@reduxjs/toolkit';

import { useUserPreferencesState } from '#/features/user-preferences/user-preferences-context';

import createSafeContext from '#/utils/createSafeContext';

import { NotificationEntry } from './types';
import { useNotificationsDrawerState } from './drawer-context';

const adapter = createEntityAdapter<NotificationEntry>({
  sortComparer: (a, b) => b.created_at.getTime() - a.created_at.getTime(),
});
const selectors = adapter.getSelectors();

const INITIAL_STATE: State = {
  notifications: adapter.getInitialState(),
};

const NOTIFICATIONS_HISTORY_LIMIT = 100;

interface State {
  readonly notifications: EntityState<NotificationEntry>;
}

interface Actions {
  readonly save: (
    entry: Omit<NotificationEntry, 'created_at' | 'is_seen'>,
  ) => void;
  readonly reset: () => void;
  readonly markAllAsRead: () => void;
}

interface View extends Omit<State, 'notifications'> {
  readonly isEmpty: boolean;
  readonly hasUnseen: boolean;
  readonly shouldDisplayToast: boolean;
  readonly notifications: readonly NotificationEntry[];
}

export const [useNotificationsView, ViewCtx] =
  createSafeContext<View>('NotificationsView');
export const [useNotificationsActions, ActionsCtx] = createSafeContext<Actions>(
  'NotificationsActions',
);

interface ProviderProps {
  readonly children: ReactNode;
}

export function NotificationsProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);

  const actions = useMemo(() => createActions(setState), []);
  const view = useView(state);

  return (
    <ViewCtx value={view}>
      <ActionsCtx value={actions}>{props.children}</ActionsCtx>
    </ViewCtx>
  );
}

export function useView(state: State): View {
  const userPreferencesState = useUserPreferencesState();
  const notificationsDrawerState = useNotificationsDrawerState();

  const notifications = selectors.selectAll(state.notifications);
  const shouldDisplayToast = shouldDisplayToastView(
    userPreferencesState,
    notificationsDrawerState,
  );
  const isEmpty = notifications.length === 0;
  const hasUnseen = hasUnseenView(state);

  return useMemo(
    () => ({
      notifications,
      isEmpty,
      shouldDisplayToast,
      hasUnseen,
    }),
    [hasUnseen, isEmpty, notifications, shouldDisplayToast],
  );
}

function shouldDisplayToastView(
  userPreferencesState: ReturnType<typeof useUserPreferencesState>,
  notificationsDrawerState: ReturnType<typeof useNotificationsDrawerState>,
) {
  if (!userPreferencesState.isToastDisplayEnabled) return false;

  return !notificationsDrawerState.isOpen;
}

function createActions(setState: Dispatch<SetStateAction<State>>): Actions {
  return {
    save(entry) {
      setState((state) => {
        const entryWithTimestamp = {
          ...entry,
          created_at: new Date(),
          is_seen: false,
        } as NotificationEntry;
        const notifications = adapter.upsertOne(
          state.notifications,
          entryWithTimestamp,
        );

        if (isOverLimit(notifications)) {
          const cappedNotifications = removeOldest(notifications);
          return { ...state, notifications: cappedNotifications };
        }

        return { ...state, notifications };
      });
    },
    reset() {
      setState(INITIAL_STATE);
    },
    markAllAsRead() {
      setState((state) => {
        let nextNotifications = state.notifications;

        const allNotifications = selectors.selectAll(state.notifications);
        for (const notification of allNotifications) {
          if (notification.is_seen) continue;

          nextNotifications = adapter.updateOne(nextNotifications, {
            id: notification.id,
            changes: { is_seen: true },
          });
        }

        return { ...state, notifications: nextNotifications };
      });
    },
  };
}

function isOverLimit(notifications: EntityState<NotificationEntry>): boolean {
  const count = selectors.selectTotal(notifications);
  return count > NOTIFICATIONS_HISTORY_LIMIT;
}

function removeOldest(
  notifications: EntityState<NotificationEntry>,
): EntityState<NotificationEntry> {
  const oldestId = selectors.selectIds(notifications).at(-1);
  if (oldestId == null) return notifications;
  return adapter.removeOne(notifications, oldestId);
}

function hasUnseenView(state: State) {
  const notifications = selectors.selectAll(state.notifications);
  if (notifications.length === 0) return false;
  return notifications.some((notification) => !notification.is_seen);
}
